import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import Panzoom, { PanzoomObject, PanzoomOptions } from '@panzoom/panzoom';
import html2canvas from 'html2canvas';
import interact from 'interactjs';
import jsPDF from 'jspdf';
import { ApiService } from '../../../service/helper/api.service';
import { BlockUiService } from '../../../service/helper/block-ui.service';
import { CommonService } from '../../../service/helper/common.service';

import { ColorPickerDirective } from 'ngx-color-picker';

@Component({
  selector: 'app-maps-print',
  templateUrl: './maps-print.component.html',
  imports: [CommonModule, FormsModule, RouterModule, ColorPickerDirective],
  styleUrls: ['./maps-print.component.scss'],
})
export class MapsPrintComponent implements OnInit, AfterViewInit, OnDestroy {
  mapImage: string[] = [];
  layerList: any[] = [];

  currentMapsImage: string = ''

  kPaperSize: any = {
    a4: { width: 210, height: 297 },
    a3: { width: 297, height: 420 },
    a0: { width: 841, height: 1189 },
  };

  data = {
    paperLayoutType: 'portrait',
    paperType: 'a4',
    dpiType: 300,
    isUseScaleBar: false,
    isUseGrid: false,
    saveType: 'pdf',
    isShowWatermark: false,
    opacity: 100

  };

  mapScale: number = 0

  @ViewChild('imagePrintMapsUpload') imagePrintMapsUpload!: ElementRef<HTMLInputElement>
  @ViewChild('paperContainer') paperContainer!: ElementRef;
  @ViewChild('paperEditor') paperEditor!: ElementRef;

  panzoomInstance!: PanzoomObject
  initialScale: number = 1
  isEditingElement: boolean = false

  textboxes: Array<
    {
      id: string;
      top: number;
      left: number;
      width: number;
      height: number;
      content: string;
      isEditing: boolean,
      opacity: number,
      isEditable: boolean,
      isDeleteable: boolean,
      dataX: number, // Store computed X
      dataY: number  // Store computed Y
    }
  > = [];
  images: Array<{
    id: string,
    src: string,
    opacity: number,
    isEditable: boolean,
    isDeleteable: boolean,
    isEditing: boolean,
  }> = [];

  contextMenu: HTMLElement | null = null;
  selectedElement: HTMLElement | null = null;
  selectedElementType: 'textbox' | 'image' | null = null;
  selectedElementDeleteAble: boolean = false;

  textColor: string = '#000000'; // Default text color
  bgColor: string = '#ffffff'; // Default background color
  opacity: number = 100;
  zoomLevel: number = 1;

  printDataKey: string = ''

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private commonService: CommonService,
    private zone: NgZone,
    private sanitizer: DomSanitizer,
    private blockUI: BlockUiService,
    private apiService: ApiService
  ) {

  }

  sanitizeHtml(content: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }


  get formattedMapScale(): string {
    return `${this.mapScale.toLocaleString()}`;
  }

  getPaperWidth(): string {
    const selectedSize = this.kPaperSize[this.data.paperType];
    const baseWidth = this.data.paperLayoutType === 'portrait' ? selectedSize.width : selectedSize.height;
    return `${baseWidth}mm`;
  }

  getPaperHeight(): string {
    const selectedSize = this.kPaperSize[this.data.paperType];
    const baseHeight = this.data.paperLayoutType === 'portrait' ? selectedSize.height : selectedSize.width;
    return `${baseHeight}mm`;
  }

  addTextbox() {
    this.textboxes.push({
      id: `textbox-${this.textboxes.length + 1}`,
      top: 10,
      left: 10,
      width: 200,
      height: 50,
      opacity: 100,
      content: 'แก้ไขข้อความ',
      isEditing: false,
      isEditable: true,
      isDeleteable: true,
      dataX: 0, // Store computed X
      dataY: 0  // Store computed Y
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && changes['data'].currentValue.paperLayoutType !== changes['data'].previousValue.paperLayoutType) {
      this.toggleWatermark(); // Reposition watermark on layout change
    }
  }

  // ✅ Warn the user before closing the tab or refreshing the page
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: Event) {
    event.preventDefault();
    (event as BeforeUnloadEvent).returnValue = 'คุณแน่ใจที่จะปิดหน้านี้หรือไม่? ข้อมูลสำหรับการพิมพ์ครั้งนี้จะไม่สามารถใช้ได้อีกครั้ง';
    const storedData = localStorage.getItem(this.printDataKey);

    if (storedData) {
      // ✅ Remove print data from localStorage when the component is destroyed
      localStorage.removeItem(this.printDataKey);
    }
  }

  ngOnDestroy(): void {

    const storedData = localStorage.getItem(this.printDataKey);

    if (storedData) {
      // ✅ Remove print data from localStorage when the component is destroyed
      localStorage.removeItem(this.printDataKey);
    }
  }



  cleanupOnClose() {
    const url = new URL(window.location.href);
    const printId = url.searchParams.get('id');

    if (printId?.startsWith('print_data_')) {
      window.addEventListener('beforeunload', () => {
        localStorage.removeItem(printId);
      });
    }
  }

  ngOnInit() {
    this.cleanupOnClose();

    this.activatedRoute.queryParams.subscribe(async (params) => {
      const blobUrls = params['blobUrls'] ? JSON.parse(decodeURIComponent(params['blobUrls'])) : [];
      this.printDataKey = params['id'];

      if (this.printDataKey) {
        const storedData = localStorage.getItem(this.printDataKey);

        if (storedData) {
          let printData = JSON.parse(storedData);
          this.layerList = printData.visibleLayers;
          this.mapScale = printData.mapScaleFactor;

          // Retrieve images from IndexedDB
          if (blobUrls.length > 0) {
            this.mapImage = blobUrls;
            this.currentMapsImage = this.mapImage[0]; // Display the first image

            if (this.layerList.length) {
              this.layerList.forEach((layer) => {

                if (layer.options?.isDrawLayer || !layer.options.layer_id) {
                  return
                }

                let pathAPI = ''

                // 1=ชั้นข้อมูล 2=ขอบเขตการปกครอง 3=ประวัติ
                switch (layer.options.layer_category) {
                  case 1:
                    pathAPI = "GetLayerDetail"
                    break;

                  case 2:
                    pathAPI = "GetLegendMainScopeGovernance"
                    break;

                  case 3:
                    pathAPI = "GetHistoryLayerDetail"
                    break;

                  default:
                    pathAPI = "GetLayerDetail"
                    break;
                }

                this.apiService.post(pathAPI, {
                  layer_id: layer.options.layer_id
                }).subscribe((res: any) => {
                  if (res && res.body) {
                    layer.options.layer_data.layer_detail = res.body
                  }
                })
              })

              this.addLayerTextboxes(this.layerList)
            }
          }

          this.currentMapsImage = this.mapImage[0];
          this.ngOnDestroy()
        } else {
          console.warn("No print data found in localStorage.");
          // this.router.navigate(['/maps']);
        }
      } else {
        console.warn("No valid printDataKey found in URL.");
        // this.router.navigate(['/maps']);
      }
    });



    this.contextMenu = document.getElementById('context-menu');

    console.log(this.mapScale, 'this.mapScale')

    document.addEventListener('click', (event) => {
      if (
        this.contextMenu &&
        event.target &&
        !(this.contextMenu.contains(event.target as Node))
      ) {
        this.contextMenu.style.display = 'none';
      }
    });

    // Prevent hiding when clicking inside the context menu
    if (this.contextMenu) {
      this.contextMenu.addEventListener('click', (event) => {
        event.stopPropagation(); // Stop click from bubbling up
      });
    }


  }

  resetContentPosition() {
    // this.textboxes.forEach((txt) => {
    //   txt.dataX = 0
    //   txt.dataY = 0
    // })

    // this.images.forEach((img) => {
    //   img.dataX = 0
    //   img.dataY = 0
    // })
  }

  addLayerTextboxes(layerList: any[]): void {
    if (!layerList || layerList.length === 0) {
      return;
    }

    const containerWidth = parseFloat(this.getPaperWidth()) * 3.78; // Convert mm to pixels
    const textboxWidth = 300; // Fixed width assumption
    const columns = 3; // 3 layers per row
    const gap = 20; // Space between columns

    layerList.forEach((layer, index) => {
      if (!layer.options.isDrawLayer && layer.options.layer_type === 'polygon') {
        console.log(layer, 'azzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzz')
        const content = this.generateLayerHTML(layer, index);

        const colIndex = index % columns; // Get column index (0,1,2,0,1,2,...)
        const rowIndex = Math.floor(index / columns); // Get row index (0,0,0,1,1,1,...)

        // Adjust X position: First item in each row starts at 0, others are positioned normally
        const initialX = colIndex === 0
          ? 0
          : ((containerWidth - (columns * (textboxWidth + gap - gap))) / 2 + (colIndex * (textboxWidth + gap))) / this.zoomLevel;

        const initialY = (50 + rowIndex * 70) / this.zoomLevel; // Stack rows dynamically


        this.textboxes.push({
          id: `textbox-${this.textboxes.length + 1}`,
          top: 10 + index * 60, // Adjust position dynamically to avoid overlap
          left: 10,
          width: 200,
          height: 50,
          opacity: 100,
          content: content, // Set individual content for each layer
          isEditing: false,
          isEditable: false,
          isDeleteable: false,
          dataX: initialX, // Store computed X
          dataY: initialY  // Store computed Y

          // dataX: 0, // Store computed X
          // dataY: 0  // Store computed Y
        });
      }
    });
  }

  generateLayerHTML(layer: any, index: number): string {

    if (this.commonService.isPropertyNullOrUndefined(layer.options.layer_data.layer_detail.legend?.config_type) || layer.options.layer_data.layer_detail.legend?.config_type == 1) {
      console.log(layer.options.layer_data.layer_name, '1')
      let property = null

      if (layer.options.layer_data.layer_detail.legend.config_type == 1) {
        property = layer.options.layer_data.layer_detail.legend?.property?.data[0]
      } else {
        property = {
          color: "#ffffff",
          value: 1,
          status: 1,
          strokeColor: "#000000",
          strokeWidth: 1,
          isUseFillColor: true,
          isUseStrokeColor: true
        }
      }

      let htmlContent: string = `
      <div style="pointer-events: none">
        <p class="px-2">ชั้นข้อมูล: <span class="color-box" style="background-color: ${property.color}; border: ${property.strokeWidth}px solid ${property.strokeColor}">&nbsp;&nbsp;&nbsp;</span> ${layer.options.layer_data.layer_name}</p>
      </div>
      `


      return htmlContent;
    } else {
      console.log(layer.options.layer_data.layer_name, '2')
      let htmlContent = `
   
        <div style="pointer-events: none">
          <p class="px-2">ชั้นข้อมูล: ${layer.options.layer_data.layer_name}</p>
          ${layer.options.layer_data.layer_detail.legend ? '<ul>' : ''}`;

      if (layer.options.layer_data.layer_detail.legend) {
        layer.options.layer_data.layer_detail.legend?.property?.data.forEach((property: any) => {
          htmlContent += `
            <p class="mb-1">
              <span class="color-box" style="
              background-color: ${property.color}; 
              border: ${property.strokeWidth}px solid ${property.strokeColor || '#000000'}
              ">&nbsp;&nbsp;&nbsp;</span>
              ${property.name}
            </p>`;
        });
      }



      htmlContent += layer.options.layer_data.layer_detail.legend ? '</ul>' : '';
      htmlContent += `</div>`;

      console.log(htmlContent)

      return htmlContent;
    }


  }

  async ngAfterViewInit() {
    await this.initPanzoom()

    this.initInteractJS()
    this.toggleWatermark()

    window.addEventListener('resize', () => {
      this.resetZoom(); // Auto-adjust on window resize
    });

    this.textboxes.push({
      id: `textbox-${this.textboxes.length + 1}`,
      top: 10,
      left: 10,
      width: 200,
      height: 50,
      opacity: 100,
      content: `มาตราส่วน 1 : ${this.formattedMapScale}`,
      isEditing: false,
      isEditable: false,
      isDeleteable: false,
      dataX: 0, // Store computed X
      dataY: 0  // Store computed Y
    });


  }

  async initPanzoom() {
    const padding = 20; // in px
    const container = this.paperEditor.nativeElement
    const content = this.paperContainer.nativeElement;

    // Get container and content sizes
    const containerWidth = container.clientWidth - (padding * 2);
    const containerHeight = container.clientHeight - (padding * 2);
    const contentWidth = content.scrollWidth;
    const contentHeight = content.scrollHeight;

    // Calculate the best initial scale to fit the content inside the container
    const scaleX = containerWidth / contentWidth;
    const scaleY = containerHeight / contentHeight;
    this.initialScale = Math.min(scaleX, scaleY); // Choose the smaller scale to fit completely

    let panzoomConfig: PanzoomOptions = {
      startScale: this.initialScale,
      excludeClass: 'no-panzoom'
    }
    this.panzoomInstance = Panzoom(this.paperContainer.nativeElement, panzoomConfig);

    this.panzoomInstance.zoom(this.initialScale);
    this.panzoomInstance.pan(0, 0);
    this.zoomLevel = this.panzoomInstance.getScale();

    // Enable zooming with mouse wheel

    this.paperContainer.nativeElement.addEventListener('click', () => {
      this.textboxes.forEach((textbox) => (textbox.isEditing = false));
    });

    this.paperContainer.nativeElement.addEventListener('wheel', (event: WheelEvent) => {
      if (!event.shiftKey || this.isEditingElement) return;

      event.preventDefault(); // Prevent default scrolling behavior
      this.panzoomInstance.zoomWithWheel(event); // Correctly apply zoom
    }, { passive: false });
  }


  initInteractJS() {
    var self = this;

    (window as any).dragMoveListener = (event: any) => {
      const target = event.target;
      let x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx / (1 / self.zoomLevel);
      let y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy / (1 / self.zoomLevel);

      target.style.transform = `translate(${x}px, ${y}px)`;
      target.setAttribute('data-x', x.toString());
      target.setAttribute('data-y', y.toString());
    }

    this.zone.runOutsideAngular(() => {
      interact('.text-container-wrapper')
        .draggable({
          listeners: {
            start: (event) => {
              self.isEditingElement = true;
              event.target.classList.add('resizing-active');
              document.body.style.userSelect = 'none'
            },
            move: (event) => {

              const target = event.target;
              // let x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx / self.zoomLevel;
              // let y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy / self.zoomLevel;

              let x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx * (1 / self.zoomLevel);
              let y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy * (1 / self.zoomLevel);


              target.style.transform = `translate(${x}px, ${y}px)`;
              target.setAttribute('data-x', x.toString());
              target.setAttribute('data-y', y.toString());
            },
            end: (event) => {
              self.isEditingElement = false;
              event.target.classList.remove('resizing-active');
              document.body.style.userSelect = 'none'

            },
          },
          inertia: true,
          modifiers: [
            interact.modifiers.restrictRect({
              restriction: 'parent',
              endOnly: true
            })
          ],


        })
        .preventDefault('never')
        .resizable({
          edges: { top: true, left: true, bottom: true, right: true },
          listeners: {
            start(event) {
              event.ppre
              self.isEditingElement = true;
              event.target.classList.add('resizing-active');
            },
            move: event => {
              const target = event.target;
              let x = parseFloat(target.getAttribute('data-x')) || 0;
              let y = parseFloat(target.getAttribute('data-y')) || 0;

              // let x = (parseFloat(target.dataset.x) || 0) + event.dx * (1 / self.zoomLevel);
              // let y = (parseFloat(target.dataset.y) || 0) + event.dy * (1 / self.zoomLevel);

              const textEl = target.querySelector('.text-preview-printmaps') as HTMLElement;
              const inputTextEl = target.querySelector('.text-input-printmaps') as HTMLTextAreaElement;


              target.style.width = `${event.rect.width / this.zoomLevel}px`;
              target.style.height = `${event.rect.height / this.zoomLevel}px`;

              if (textEl) {
                textEl.style.width = `${event.rect.width / this.zoomLevel}px`;
                textEl.style.height = `${event.rect.height / this.zoomLevel}px`;
              }

              if (inputTextEl) {
                inputTextEl.style.width = `${event.rect.width / this.zoomLevel}px`;
                inputTextEl.style.height = `${event.rect.height / this.zoomLevel}px`;

              }

              if (event.edges.left) {
                x += event.deltaRect.left / this.zoomLevel;
              }
              if (event.edges.top) {
                y += event.deltaRect.top / this.zoomLevel;
              }

              target.style.transform = `translate(${x}px, ${y}px)`;
              target.setAttribute('data-x', x.toString());
              target.setAttribute('data-y', y.toString());
              // target.textContent = `${Math.round(newWidth)} x ${Math.round(newHeight)}`;
            },
            end(event) {
              self.isEditingElement = false;
              event.target.classList.remove('resizing-active');
            },
          },
          modifiers: [
            interact.modifiers.restrictEdges({
              outer: 'parent',
            }),
            // Dynamically restrict size based on text content
            interact.modifiers.restrictSize({
              min: { width: 1, height: 1 }, // This will be overridden dynamically
            }),
          ],
          inertia: true,
          allowFrom: '*',
        })

    })

    interact('.draggable-resizable-container')
      .draggable({
        listeners: {
          start(event) {
            self.isEditingElement = true;
            // Show border when dragging starts
            event.target.classList.add('resizing-active');
          },
          move(event) {
            const target = event.target;
            // const x = (parseFloat(target.dataset.x) || 0) + event.dx;
            // const y = (parseFloat(target.dataset.y) || 0) + event.dy;

            let x = (parseFloat(target.dataset.x) || 0) + event.dx * (1 / self.zoomLevel);
            let y = (parseFloat(target.dataset.y) || 0) + event.dy * (1 / self.zoomLevel);

            target.style.transform = `translate(${x}px, ${y}px)`;
            target.dataset.x = x.toString();
            target.dataset.y = y.toString();
          },

          end(event) {
            self.isEditingElement = false;
            // Hide border when dragging ends
            event.target.classList.remove('resizing-active');
          },
        },
        modifiers: [
          interact.modifiers.restrictRect({
            restriction: 'parent',
            endOnly: true,
          }),
        ],
        inertia: true,
      })
      .preventDefault('never')
      .resizable({
        edges: { top: true, left: true, bottom: true, right: true },
        listeners: {
          start(event) {
            self.isEditingElement = true;
            event.target.classList.add('resizing-active');

            // Store the original aspect ratio
            const target = event.target;
            target.dataset.aspectRatio = (target.offsetWidth / target.offsetHeight).toString();
          },
          move: event => {
            const target = event.target;
            let x = parseFloat(target.getAttribute('data-x')) || 0;
            let y = parseFloat(target.getAttribute('data-y')) || 0;

            // let x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx * (1 / self.zoomLevel);
            // let y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy * (1 / self.zoomLevel);


            const imgEL = target.querySelector('.draggable-image') as HTMLImageElement;

            target.style.width = `${event.rect.width / this.zoomLevel}px`;
            target.style.height = `${event.rect.height / this.zoomLevel}px`;

            if (imgEL) {
              imgEL.style.width = `${event.rect.width / this.zoomLevel}px`;
              imgEL.style.height = `${event.rect.height / this.zoomLevel}px`;
            }

            if (event.edges.left) {
              x += event.deltaRect.left / this.zoomLevel;
            }
            if (event.edges.top) {
              y += event.deltaRect.top / this.zoomLevel;
            }

            target.style.transform = `translate(${x}px, ${y}px)`;
            target.setAttribute('data-x', x.toString());
            target.setAttribute('data-y', y.toString());
            // target.textContent = `${Math.round(newWidth)} x ${Math.round(newHeight)}`;
          },

          end(event) {
            self.isEditingElement = false;
            event.target.classList.remove('resizing-active');
          },
        },
        modifiers: [
          interact.modifiers.restrictEdges({
            outer: 'parent',
          }),
          interact.modifiers.restrictSize({
            min: { width: 1, height: 1 },
          }),
        ],
        inertia: true,
      });

  }

  getHighestZIndex(): number {
    const elements = document.querySelectorAll('.text-container-wrapper, .draggable-resizable-container');
    let highestZIndex = 0;

    elements.forEach((el) => {
      const zIndex = parseInt(window.getComputedStyle(el).zIndex);
      if (!isNaN(zIndex) && zIndex > highestZIndex) {
        highestZIndex = zIndex;
      }
    });

    return highestZIndex;
  }

  onImageUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        // Create an image element to get its natural dimensions
        const tempImg = new Image();
        tempImg.src = reader.result as string;
        tempImg.onload = () => {
          const container = document.querySelector('.image-container') as HTMLElement;
          if (!container) return;

          const newImage = {
            id: `${this.images.length + 1}-el`,
            src: reader.result as string,
            opacity: 100,
            isEditable: true,
            isDeleteable: true,
            isEditing: true,
          };

          this.images.push(newImage);
          input.value = ''

        };
      };
      reader.readAsDataURL(file);
    }
  }



  addImage() {
    if (this.imagePrintMapsUpload) {
      this.imagePrintMapsUpload.nativeElement.click()

    }
  }

  collectElementData() {
    const container = document.querySelector('.image-container');
    const elements = container?.querySelectorAll('.text-container, .draggable-image');

    const data = Array.from(elements || []).map((el) => {
      const style = window.getComputedStyle(el);
      const transform = style.transform;
      const rect = el.getBoundingClientRect();

      return {
        id: el.id,
        type: el.tagName.toLowerCase(),
        content: el.tagName.toLowerCase() === 'input' ? (el as HTMLInputElement).value : '',
        src: el.tagName.toLowerCase() === 'img' ? (el as HTMLImageElement).src : '',
        width: rect.width,
        height: rect.height,
        transform: transform,
      };
    });

    return data;
  }

  async renderToCanvas() {
    const container = document.querySelector('.image-container') as HTMLElement;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const elements = this.collectElementData();

    if (!context) {
      return
    }

    for (const el of elements) {


      if (el.type === 'input') {
        context.fillStyle = 'blue';
        context.fillRect(
          parseFloat(el.transform.split(',')[4] || '0'),
          parseFloat(el.transform.split(',')[5] || '0'),
          el.width,
          el.height
        );
        context.fillStyle = 'white';
        context.fillText(el.content, 10, 30); // Adjust as needed
      } else if (el.type === 'img') {
        const image = new Image();
        image.src = el.src;
        await new Promise((resolve) => {
          image.onload = () => {
            context?.drawImage(
              image,
              parseFloat(el.transform.split(',')[4] || '0'),
              parseFloat(el.transform.split(',')[5] || '0'),
              el.width,
              el.height
            );
            resolve(true);
          };
        });
      }
    }

    return canvas;
  }


  async printMap() {

    // Adjust dimensions based on paper type and layout
    const dimensions = this.getPaperDimensions(this.data.paperType, this.data.paperLayoutType);

    // Capture the map element
    const mapElement = document.querySelector('.image-container') as HTMLElement;
    if (!mapElement) {
      console.error('Map element not found!');
      return;
    }

    // Capture as image
    const canvas = await html2canvas(mapElement, {
      scale: this.data.dpiType / 72, // Scale based on DPI
    });

    const imgData = canvas.toDataURL('image/' + this.data.saveType);

    if (this.data.saveType === 'pdf') {
      // Save as PDF
      const pdf = new jsPDF(this.data.paperLayoutType as any, 'mm', this.data.paperType.toUpperCase());
      pdf.addImage(imgData, 'JPEG', 0, 0, dimensions.width, dimensions.height);
      pdf.save('map.pdf');
    } else {
      // Save as image
      const link = document.createElement('a');
      link.href = imgData;
      link.download = `map.${this.data.saveType}`;
      link.click();
    }
  }

  getPaperDimensions(paperType: string, layout: string): { width: number; height: number } {
    const dimensions: any = {
      a3: { width: 297, height: 420 },
      a4: { width: 210, height: 297 },
      a0: { width: 841, height: 1189 },
    };

    const size = dimensions[paperType.toLowerCase()];
    if (!size) {
      throw new Error('Unsupported paper type');
    }

    return layout === 'landscape'
      ? { width: size.height, height: size.width }
      : size;
  }

  base64ToFile(base64: string, filename: string): File {
    const arr = base64.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);

    if (!mimeMatch || !mimeMatch[1]) {
      throw new Error('Invalid Base64 string'); // Handle invalid Base64 input
    }

    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  alignText(alignType: 'left' | 'center' | 'right') {
    if (this.selectedElement) {
      this.selectedElement.style.textAlign = alignType;
    }
  }

  updateTextboxContent(event: Event, id: string): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const textbox = this.textboxes.find((tb) => tb.id === id);
    if (textbox) {
      textbox.content = inputValue;
    }
  }

  onRightClick(event: MouseEvent, element: EventTarget | null, elData: any) {
    event.preventDefault();

    if (!element || !(element instanceof HTMLElement)) return;

    this.selectedElement = element;

    // Determine if it's an image or a textbox
    if (element.classList.contains('text-container-wrapper') || element.classList.contains('text-preview-printmaps')) {

      this.selectedElementType = 'textbox';

      // Get font size & color for textboxes
      const computedStyle = window.getComputedStyle(this.selectedElement);
      const currentFontSize = parseInt(computedStyle.fontSize);
      const currentColor = computedStyle.color;
      const currentBgColor = computedStyle.backgroundColor;
      const currentOpacity = computedStyle.opacity;


      this.opacity = parseFloat(currentOpacity) * 100

      // Set values in input fields
      const fontSizeInput = document.getElementById('font-size-input') as HTMLInputElement;

      if (fontSizeInput) fontSizeInput.value = currentFontSize.toString();
      this.textColor = currentColor
      this.bgColor = currentBgColor
    } else if (element.classList.contains('draggable-resizable-container') || element.classList.contains('draggable-image')) {
      this.selectedElementType = 'image'; // Set type to image
      const computedStyle = window.getComputedStyle(this.selectedElement);
      const currentOpacity = computedStyle.opacity;

      this.opacity = parseFloat(currentOpacity) * 100
    } else {
      this.selectedElementType = null;
      return; // Exit if it's neither a textbox nor an image
    }


    this.selectedElementDeleteAble = elData.isDeleteable

    if (this.contextMenu) {
      const editor = this.paperEditor.nativeElement;
      const cursorX = event.clientX;
      const cursorY = event.clientY;

      // Prepare for measuring
      this.contextMenu.style.top = `-9999px`;
      this.contextMenu.style.left = `-9999px`;
      this.contextMenu.style.display = 'block';
      this.contextMenu.style.visibility = 'hidden';

      // Wait for DOM layout
      requestAnimationFrame(() => {
        const menuRect = this.contextMenu!.getBoundingClientRect();
        const editorRect = editor.getBoundingClientRect();

        let posX = cursorX;
        let posY = cursorY;

        // Adjust X if overflowing right
        if (posX + menuRect.width > editorRect.right) {
          posX = editorRect.right - menuRect.width - 10;
        }

        // Adjust X if overflowing left
        if (posX < editorRect.left) {
          posX = editorRect.left + 10;
        }

        // Adjust Y if overflowing bottom
        if (posY + menuRect.height > editorRect.bottom) {
          posY = cursorY - menuRect.height - 10;
        }

        // Adjust Y if overflowing top
        if (posY < editorRect.top) {
          posY = editorRect.top + 10;
        }

        // Set final position and show
        this.contextMenu!.style.left = `${posX}px`;
        this.contextMenu!.style.top = `${posY}px`;
        this.contextMenu!.style.visibility = 'visible';
      });
    }
  }

  copyComputedStyles(fromEl: HTMLElement, toEl: HTMLElement): void {
    const computedStyles = window.getComputedStyle(fromEl);
    for (let i = 0; i < computedStyles.length; i++) {
      const prop = computedStyles[i] as any;
      toEl.style[prop] = computedStyles.getPropertyValue(prop);
    }
  }


  saveElementAsImage() {
    setTimeout(() => {
      if (this.selectedElement) {
        const originalElement = this.selectedElement as HTMLElement;
        const clone = originalElement.cloneNode(true) as HTMLElement;

        clone.style.width = 'fit-content';
        clone.style.margin = '1rem';

        document.body.appendChild(clone);

        html2canvas(clone, {
          width: clone.offsetWidth,
          height: clone.offsetHeight,
          scale: 2,
          useCORS: true,
        }).then(canvas => {
          const image = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.href = image;
          link.download = 'captured-element.png';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          document.body.removeChild(clone)
        }).catch(error => {
          console.error('Error capturing element:', error);
          document.body.removeChild(clone)
        });
      } else {
        console.warn('No element selected to capture');
      }
    }, 200);

    console.log(this.selectedElement)
  }

  // Change Font Size
  changeFontSize(event: Event) {
    if (this.selectedElement) {
      const inputElement = event.target as HTMLInputElement;
      const newSize = parseInt(inputElement.value, 10);

      this.selectedElement.style.fontSize = `${inputElement.value}px`;
      this.selectedElement.style.lineHeight = `${newSize * 1.2}px`;
    }
  }

  // Change Font Color
  changeFontColor(color: string) {
    if (this.selectedElement) {
      this.selectedElement.style.color = color;
    }
  }

  changeBackgroundColor(color: string) {
    if (this.selectedElement) {
      this.selectedElement.style.backgroundColor = color;
    }
  }

  changeOpacity(number: string) {
    if (this.selectedElement) {
      this.selectedElement.style.opacity = number + '%';
      this.opacity = parseInt(number);
    }
  }

  deleteItem() {
    if (!this.selectedElement) return;
    if (!this.selectedElementDeleteAble) return;

    const elementId = this.selectedElement.getAttribute('data-elementid');
    const isTextBox = this.textboxes.some((textbox) => textbox.id === elementId);
    const isImage = this.images.some((image) => image.id === elementId);

    if (isTextBox) {
      this.textboxes = this.textboxes.filter((textbox) => textbox.id !== elementId);
    } else if (isImage) {
      this.images = this.images.filter((image) => image.id !== elementId);
    } else {
      return
    }

    this.selectedElement.remove()
    this.selectedElement = null; // Clear selection after deletion
    this.selectedElementDeleteAble = false; // Clear selection after deletion
    this.hideContextMenu();
  }

  hideContextMenu() {
    if (this.contextMenu) {
      this.contextMenu.style.display = 'none';
    }
  }

  bringImageToFront() {
    if (!this.selectedElement) return;
    const wrapper = this.selectedElement.closest('.draggable-resizable-container') as HTMLElement;

    if (wrapper) {
      wrapper.style.zIndex = `${this.getHighestZIndex() + 1}`;
    }

    this.hideContextMenu();
  }

  bringTextToFront() {
    if (!this.selectedElement) return;
    const wrapper = this.selectedElement.closest('.text-container-wrapper') as HTMLElement;

    if (wrapper) {
      wrapper.style.zIndex = `${this.getHighestZIndex() + 1}`;
    }

    this.hideContextMenu();
  }

  sendTextToBack() {
    if (!this.selectedElement) return;
    const wrapper = this.selectedElement.closest('.text-container-wrapper') as HTMLElement;

    if (wrapper) {
      const allTextWrappers = document.querySelectorAll('.text-container-wrapper');

      allTextWrappers.forEach((el) => {
        if (el !== wrapper) {
          const currentZ = parseInt((el as HTMLElement).style.zIndex || '0', 10);
          (el as HTMLElement).style.zIndex = (currentZ + 1).toString();
        }
      });

      wrapper.style.zIndex = '1';
    }

    this.hideContextMenu(); // Hide menu after clicking
  }

  sendImageToBack() {
    if (!this.selectedElement) return;
    const wrapper = this.selectedElement.closest('.draggable-resizable-container') as HTMLElement;

    if (wrapper) {
      const allImageWrappers = document.querySelectorAll('.draggable-resizable-container');

      allImageWrappers.forEach((el) => {
        if (el !== wrapper) {
          const currentZ = parseInt((el as HTMLElement).style.zIndex || '0', 10);
          (el as HTMLElement).style.zIndex = (currentZ + 1).toString();
        }
      });

      wrapper.style.zIndex = '1';
    }

    this.hideContextMenu(); // Hide menu after clicking
  }


  toggleTextStyle(style: 'bold' | 'italic' | 'underline') {
    if (!this.selectedElement) return;

    let currentStyle = this.selectedElement.style.fontWeight;
    let currentItalic = this.selectedElement.style.fontStyle;
    let currentUnderline = this.selectedElement.style.textDecoration;

    switch (style) {
      case 'bold':
        this.selectedElement.style.fontWeight = currentStyle === 'bold' ? 'normal' : 'bold';
        break;
      case 'italic':
        this.selectedElement.style.fontStyle = currentItalic === 'italic' ? 'normal' : 'italic';
        break;
      case 'underline':
        this.selectedElement.style.textDecoration = currentUnderline === 'underline' ? 'none' : 'underline';
        break;
    }
  }

  enableEditing(id: string): void {
    const textbox = this.textboxes.find((tb) => tb.id === id);
    if (textbox) {
      textbox.isEditing = true;

      this.cdr.detectChanges();

      setTimeout(() => {
        const textBoxEl = document.getElementById(`${textbox.id}-text-input`)
        if (textBoxEl) {
          textBoxEl.focus();
        }
      }, 50);
    }
  }

  disableEditing(id: string): void {
    const textbox = this.textboxes.find((tb) => tb.id === id);
    if (textbox) {
      textbox.isEditing = false;
    }
  }

  zoomIn() {
    this.panzoomInstance.zoomIn();
    this.zoomLevel = this.panzoomInstance.getScale();
  }

  zoomOut() {
    this.panzoomInstance.zoomOut();
    this.zoomLevel = this.panzoomInstance.getScale();
  }

  resetZoom(scale?: number) {
    setTimeout(() => {
      const padding = 20; // in px
      const container = this.paperEditor.nativeElement
      const content = this.paperContainer.nativeElement;

      // Get container and content sizes
      const containerWidth = container.clientWidth - (padding * 2);
      const containerHeight = container.clientHeight - (padding * 2);
      const contentWidth = content.scrollWidth;
      const contentHeight = content.scrollHeight;

      // Calculate the best initial scale to fit the content inside the container
      const scaleX = containerWidth / contentWidth;
      const scaleY = containerHeight / contentHeight;
      let initialScale = Math.min(scaleX, scaleY); // Choose the smaller scale to fit completely

      let scaleValue = scale ? scale : initialScale

      this.panzoomInstance.zoom(scaleValue, { animate: true }); // Reset to default zoom level (scale 1)
      this.panzoomInstance.pan(0, 0); // Reset panning to center
    }, 50)
  }

  centerPaper() {
    const container = document.querySelector('.col-lg-9') as HTMLElement;
    if (!container) return;

    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.justifyContent = 'center';
    container.style.overflow = 'auto'; // Allow scrolling if needed
  }

  toggleWatermark() {
    let watermark = document.getElementById('watermark-image') as HTMLImageElement;
    const paperContainer = document.querySelector('.image-container');

    if (!paperContainer) return;

    if (this.data.isShowWatermark) {
      if (!watermark) {
        // Create the watermark image element only if it doesn't exist
        watermark = document.createElement('img');
        watermark.id = 'watermark-image';
        watermark.src = 'assets/imgs/doae-full-logo.png'; // Watermark Image Path
        paperContainer.appendChild(watermark);
      }

      // Apply styles dynamically based on paper layout
      watermark.style.position = 'absolute';
      watermark.style.opacity = '0.15'; // Light transparency
      watermark.style.pointerEvents = 'none'; // Prevent user interaction
      watermark.style.zIndex = '0'; // Keep behind content

      if (this.data.paperLayoutType === 'portrait') {
        // Bottom-right watermark for portrait mode
        watermark.style.bottom = 'unset';
        watermark.style.right = 'unset';
        watermark.style.top = '10px';
        watermark.style.left = '10px';
        watermark.style.transform = 'none';
        watermark.style.width = 'auto';
        watermark.style.maxWidth = '300px';
      } else {
        // Centered and stretched for landscape mode
        watermark.style.top = '50%';
        watermark.style.left = '50%';
        watermark.style.transform = 'translate(-50%, -50%)';
        watermark.style.width = '80%'; // Cover most of the paper width
        watermark.style.height = 'auto';
        watermark.style.bottom = 'unset';
        watermark.style.right = 'unset';
        watermark.style.maxWidth = 'unset';
      }
    } else {
      // Remove watermark if unchecked
      if (watermark) {
        watermark.remove();
      }
    }
  }


  async saveAsImage() {
    this.blockUI.start()
    const paperContainer = document.querySelector('.image-container') as HTMLElement;
    const deleteButtons = document.querySelectorAll('.delete-button'); // Select all delete buttons

    if (!paperContainer) {
      this.blockUI.stop()
      console.error('Paper container not found!');
      return;
    }

    // Hide delete buttons
    deleteButtons.forEach((button) => {
      (button as HTMLElement).style.visibility = 'hidden';
    });

    this.resetZoom(1)



    // Force Angular to re-render the dimensions
    await new Promise((resolve) => setTimeout(resolve, 50));

    setTimeout(() => {
      // Capture the content using html2canvas
      html2canvas(paperContainer, {
        scale: 2, // Improve quality
        useCORS: true, // Handle cross-origin images
      })
        .then(async (canvas) => {
          canvas.toBlob(async (blob) => {
            if (!blob) {
              console.error('Failed to capture image as Blob!');
              return;
            }

            // Gather options from UI
            const options = {
              paperLayoutType: this.data.paperLayoutType || 'portrait',
              paperType: this.data.paperType || 'a4',
              dpiType: this.data.dpiType || 300,
              isUseScaleBar: this.data.isUseScaleBar || false,
              isUseGrid: this.data.isUseGrid || false,
              saveType: this.data.saveType || 'pdf',
            };

            try {
              if (options.saveType === 'pdf') {
                // Convert to PDF
                const pdfBlob = await this.convertBlobToPdf(blob, options);
                this.downloadFile(pdfBlob, `Map-${new Date().toISOString().slice(0, 10)}.pdf`);
              } else {
                // Convert to image (jpeg/png/tiff)
                const imageBlob = await this.convertBlobToImage(blob, options);
                this.downloadFile(
                  imageBlob,
                  `Map-${new Date().toISOString().slice(0, 10)}.${options.saveType}`
                );
              }

              this.blockUI.stop()
            } catch (error) {
              this.blockUI.stop()
              console.error('Error converting image:', error);
            }
          }, 'image/png');
        })
        .catch((error) => {
          this.blockUI.stop()
          console.error('Error capturing the image:', error);
          this.resetZoom()
        });

    }, 300)

  }

  // Utility to download a file
  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url); // Clean up URL object
  }

  async convertBlobToPdf(blob: Blob, options: any): Promise<Blob> {
    const { paperLayoutType, paperType, dpiType, isUseScaleBar, isUseGrid } = options;

    // Map paper sizes
    const paperSizes: any = {
      a3: { width: 297, height: 420 },
      a4: { width: 210, height: 297 },
      a0: { width: 841, height: 1189 },
    };

    let { width, height } = paperSizes[paperType.toLowerCase()];

    // Swap dimensions for landscape layout
    if (paperLayoutType.toLowerCase() === 'landscape') {
      [width, height] = [height, width];
    }

    // Convert mm to points (1 mm = 2.83465 pts)
    width *= 2.83465;
    height *= 2.83465;

    // Create jsPDF document
    const doc = new jsPDF({
      orientation: paperLayoutType.toLowerCase(),
      unit: 'pt',
      format: [width, height],
    });

    // Convert blob to image data URL
    const imgData = await this.blobToDataURL(blob);

    // Add image to PDF
    doc.addImage(imgData, 'PNG', 0, 0, width, height);

    return doc.output('blob');
  }

  // Helper to convert blob to a Data URL
  private blobToDataURL(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async convertBlobToImage(blob: Blob, options: any): Promise<Blob> {
    const { paperLayoutType, paperType, dpiType, saveType } = options;

    // Map paper sizes
    const paperSizes: any = {
      a3: { width: 297, height: 420 },
      a4: { width: 210, height: 297 },
      a0: { width: 841, height: 1189 },
    };

    let { width, height } = paperSizes[paperType.toLowerCase()];

    // Swap dimensions for landscape layout
    if (paperLayoutType.toLowerCase() === 'landscape') {
      [width, height] = [height, width];
    }

    // Adjust dimensions for DPI
    const dpiFactor = dpiType / 72; // Scale factor
    width *= dpiFactor;
    height *= dpiFactor;

    // Create canvas for resizing
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');

    // Draw the image onto the canvas
    const img = new Image();
    img.src = await this.blobToDataURL(blob);
    await new Promise((resolve) => (img.onload = resolve));
    ctx.drawImage(img, 0, 0, width, height);

    // Convert canvas to desired format
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (outputBlob) => {
          if (outputBlob) resolve(outputBlob);
          else reject(new Error('Failed to convert canvas to blob'));
        },
        `image/${saveType}`,
        0.95 // Image quality
      );
    });
  }

  changeMapImageType() {
    if (!this.mapImage) return

    if (!this.data.isUseGrid && !this.data.isUseScaleBar) {
      // Version 1: Just the map
      this.currentMapsImage = this.mapImage[0]
    } else if (!this.data.isUseGrid && this.data.isUseScaleBar) {
      // Version 2: Map with scale bar
      this.currentMapsImage = this.mapImage[1]
    } else if (this.data.isUseGrid && !this.data.isUseScaleBar) {
      // Version 3: Map with grid
      this.currentMapsImage = this.mapImage[2]
    } else if (this.data.isUseGrid && this.data.isUseScaleBar) {
      // Version 4: Map with grid and scale bar
      this.currentMapsImage = this.mapImage[3]
    }
  }

}
